import type {
  BundleMessage,
  BundlerCapabilitiesOptions,
  BundlerResult,
  CloudpackConfig,
  PackageDefinitionsCache,
} from '@ms-cloudpack/common-types';
import { generateESMStubFromExports } from '@ms-cloudpack/esm-stub-utilities';
import { writeFile } from 'fs/promises';
import path from 'path';
import { toNormalizedEntryKey } from './toNormalizedEntryKey.js';
import { flattenExportsMap, getExportsMap } from '@ms-cloudpack/package-utilities';
import { slash } from '@ms-cloudpack/path-string-parsing';

const errorSource = 'density-entry-stub';
export async function writeStubForDensityEntries(params: {
  options: {
    packageName: string;
    packagePath: string;
    result: BundlerResult;
    densityOptions: NonNullable<BundlerCapabilitiesOptions['density']>;
  };
  context: {
    packages: PackageDefinitionsCache;
    config: CloudpackConfig;
  };
}): Promise<{ errors: BundleMessage[]; warnings: BundleMessage[] }> {
  const { options, context } = params;
  const { packagePath, packageName, result } = options;
  const exportsMap = await getExportsMap({ packagePath }, context);
  const densityCondition = ['density'];
  const densityExportsMap = flattenExportsMap(exportsMap, {
    requiredConditions: densityCondition,
    conditions: densityCondition,
  });

  if (Object.keys(densityExportsMap).length === 0 && options.densityOptions === true) {
    return { errors: [], warnings: [] };
  }

  if (!result.outputFiles) {
    return {
      errors: [
        {
          source: errorSource,
          text: 'No output files found in the result.',
        },
      ],
      warnings: [],
    };
  }

  if (!result.outputPath) {
    return {
      errors: [
        {
          source: errorSource,
          text: 'No output path found in the result.',
        },
      ],
      warnings: [],
    };
  }

  const errors: BundleMessage[] = [];
  const warnings: BundleMessage[] = [];

  const entriesToStub = Object.entries(densityExportsMap);

  if (typeof options.densityOptions === 'object' && Array.isArray(options.densityOptions.entries)) {
    // Add entries that are in the options list
    const flattenedExportsMap = flattenExportsMap(exportsMap);

    for (const key of options.densityOptions.entries) {
      const normalizedKey = toNormalizedEntryKey(key);
      if (!flattenedExportsMap[normalizedKey]) {
        warnings.push({
          text: `Entry "${key}" from density capability not found in package exports map. Available entries: ${Object.keys(flattenedExportsMap).join(', ')}`,
          source: errorSource,
        });
        continue;
      }

      if (densityExportsMap[normalizedKey]) {
        warnings.push({
          text: `Entry "${key}" from density capability already has a "density" condition in exports map, skipping custom entry`,
          source: errorSource,
        });
        continue;
      }

      const originalPath = flattenedExportsMap[normalizedKey];
      // Remove source extension if available and add .js for density stub
      const densityPath = `${toNormalizedEntryKey(originalPath)}.js`;

      entriesToStub.push([normalizedKey, densityPath]);
    }
  }

  // Generate density stubs for each entry
  for (const [key, value] of entriesToStub) {
    try {
      const entryPath = toNormalizedEntryKey(value);
      const outputFile = result.outputFiles.find((o) => o.outputPath === value);
      if (!outputFile) {
        const availableOutputs = result.outputFiles.map((f) => f.outputPath).join(', ');
        errors.push({
          text: `No output file found for density entry "${key}" (expected: ${entryPath}). Available output files: ${availableOutputs}. This might indicate a bundler configuration issue or the entry was not processed correctly.`,
          source: errorSource,
        });
        continue;
      }

      const moduleId = key === '.' ? options.packageName : path.join(packageName, entryPath);

      // generate new stab file for the external entry by using output.exports
      // found the output file. This file need to be replaced.
      const stub = generateESMStubFromExports({
        export: {
          type: outputFile.exports ? 'object' : 'other',
          // These exports are not technically correct since they might include a default export from the esm bundle
          // but density returns packages without a default.
          keys: outputFile.exports?.filter((e) => e !== 'default') ?? [],
        },
        importStatement: `const moduleExport = requireDensityModule("${slash(moduleId)}");`,
      });

      await writeFile(path.join(result.outputPath, outputFile.outputPath), stub);
    } catch (e) {
      errors.push({
        text: `Failed to create density stub for entry "${key}": ${e instanceof Error ? e.message : String(e)}.`,
        source: errorSource,
      });
    }
  }

  return { errors, warnings };
}
